import React from 'react';
import Image from 'next/image';
import PropTypes from 'prop-types';
import { isCDNImage, getImageDetails } from '../../utils/util';

function SmartImage({
  src,
  alt,
  width = 800, // Default width directly in the parameters
  height = 600, // Default height directly in the parameters
  className = '', // Default className directly in the parameters
  quality = 75, // Default quality
  unoptimized = false, // Default unoptimized false
  ...props
}) {
  const { imageSrc, imageWidth, imageHeight } = getImageDetails(
    src,
    width,
    height
  );
  const { ...imgProps } = props;
  // Function to determine if the src is a CDN URL
  const shouldFill =
    (typeof width === 'string' && width.trim() === '100%') || !width || !height;
  // If it's a CDN image, use next/image, otherwise use a regular img tag
  if (isCDNImage(src)) {
    return shouldFill ? (
      <Image
        src={imageSrc}
        alt={alt}
        quality={quality} // Passing quality here
        fill
        style={{ objectFit: 'cover' }}
        className={className}
        unoptimized={unoptimized}
        {...props} // Spread other props, like style or additional attributes
      />
    ) : (
      <Image
        src={imageSrc}
        alt={alt}
        quality={quality} // Passing quality here
        width={imageWidth}
        height={imageHeight}
        className={className}
        unoptimized={unoptimized}
        {...props} // Spread other props, like style or additional attributes
      />
    );
  }
  // For regular HTML <img> tag, delete 'priority' from imgProps
  if (imgProps?.priority) {
    delete imgProps.priority;
  }
  // Remove 'priority' to avoid passing it to <img>
  return (
    <img
      src={imageSrc}
      alt={alt}
      width={imageWidth}
      height={imageHeight}
      className={className}
      {...imgProps}
    />
  );
}

// PropTypes for type checking
SmartImage.propTypes = {
  src: PropTypes.oneOfType([
    PropTypes.string, // If the source is a string URL
    PropTypes.shape({
      // If the source is an object
      src: PropTypes.string.isRequired, // Object's src property is required
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      blurWidth: PropTypes.number,
      blurHeight: PropTypes.number,
    }),
  ]).isRequired, // Image source can be a string or an object, and it's required

  alt: PropTypes.string.isRequired, // Alt text, required
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Optional width
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Optional height
  className: PropTypes.string, // Optional className
  quality: PropTypes.number, // Add quality as a number prop
  unoptimized: PropTypes.bool, // Added unoptimized as boolean prop
  // Any other additional props that could be passed to img or next/image
};

export default SmartImage;
